import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Eye, EyeOff, ArrowRight, ArrowLeft } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import api, { setAuthToken } from "../lib/api";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_health-nexus-4/artifacts/5uryz4az_ChatGPT%20Image%20Feb%208%2C%202026%2C%2003_09_14%20PM.png";

export default function AuthPage() {
  const [searchParams] = useSearchParams();
  const defaultTab = searchParams.get("mode") === "register" ? "register" : "login";
  const navigate = useNavigate();
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [regForm, setRegForm] = useState({ name: "", email: "", password: "" });
  const [resetView, setResetView] = useState(null); // null | "email" | "code"
  const [resetEmail, setResetEmail] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [resetPassword, setResetPassword] = useState("");
  const [resetMsg, setResetMsg] = useState("");

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await api.post("/auth/login", loginForm);
      setAuthToken(res.data.token);
      localStorage.setItem("vita_user", JSON.stringify(res.data.user));
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await api.post("/auth/register", regForm);
      setAuthToken(res.data.token);
      localStorage.setItem("vita_user", JSON.stringify(res.data.user));
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError(""); setResetMsg(""); setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: resetEmail });
      setResetMsg("Check your email for the reset code.");
      setResetView("code");
    } catch (err) { setError(err.response?.data?.detail || "Failed"); }
    setLoading(false);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError(""); setResetMsg(""); setLoading(true);
    try {
      await api.post("/auth/reset-password", { token: resetToken, password: resetPassword });
      setResetMsg("Password reset! You can now log in.");
      setTimeout(() => { setResetView(null); setResetMsg(""); }, 2000);
    } catch (err) { setError(err.response?.data?.detail || "Invalid or expired code"); }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex" data-testid="auth-page">
      {/* Left Panel - Decorative */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#0B6E4F] relative overflow-hidden items-center justify-center">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-72 h-72 rounded-full bg-white/20" />
          <div className="absolute bottom-20 right-20 w-96 h-96 rounded-full bg-white/10" />
        </div>
        <div className="relative z-10 text-white text-center px-12">
          <img src={LOGO_URL} alt="VittaNex" className="h-24 w-24 rounded-full mx-auto mb-8 shadow-2xl" />
          <h1 className="font-['Outfit'] text-4xl font-bold mb-4">Welcome to VittaNex</h1>
          <p className="text-lg text-white/80 leading-relaxed max-w-md">
            Join a global community focused on Health and Fitness. Your transformation starts here.
          </p>
        </div>
      </div>

      {/* Right Panel - Auth Form */}
      <div className="flex-1 flex items-center justify-center px-6 py-12 bg-white">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-8 justify-center">
            <img src={LOGO_URL} alt="VittaNex" className="h-12 w-12 rounded-full" />
            <span className="font-['Outfit'] font-bold text-2xl">
              <span className="text-[#0B6E4F]">Vita</span>
              <span className="text-[#1B3A5C]">Nexus</span>
            </span>
          </div>

          <Tabs defaultValue={defaultTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-8 bg-[#F2F9F6] rounded-xl p-1">
              <TabsTrigger value="login" data-testid="login-tab" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm font-semibold">
                Log In
              </TabsTrigger>
              <TabsTrigger value="register" data-testid="register-tab" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm font-semibold">
                Sign Up
              </TabsTrigger>
            </TabsList>

            {error && (
              <div className="mb-4 p-3 rounded-xl bg-red-50 border border-red-100 text-red-600 text-sm" data-testid="auth-error">
                {error}
              </div>
            )}

            <TabsContent value="login">
              {resetView === "email" ? (
                <form onSubmit={handleForgotPassword} className="space-y-5">
                  <button type="button" onClick={() => { setResetView(null); setError(""); setResetMsg(""); }} className="flex items-center gap-1 text-sm text-slate-500 hover:text-[#0B6E4F] mb-2">
                    <ArrowLeft className="h-4 w-4" /> Back to login
                  </button>
                  <p className="text-sm text-slate-600">Enter your email and we'll send you a reset code.</p>
                  <div>
                    <Label className="text-sm font-medium text-slate-700">Email</Label>
                    <Input type="email" value={resetEmail} onChange={(e) => setResetEmail(e.target.value)} placeholder="you@example.com" className="mt-1.5 h-12 rounded-xl bg-slate-50 border-slate-200" data-testid="forgot-email" required />
                  </div>
                  <Button type="submit" disabled={loading} data-testid="forgot-submit-btn" className="w-full h-12 bg-[#0B6E4F] hover:bg-[#095A40] text-white rounded-full font-semibold">
                    {loading ? "Sending..." : "Send Reset Code"}
                  </Button>
                </form>
              ) : resetView === "code" ? (
                <form onSubmit={handleResetPassword} className="space-y-5">
                  <button type="button" onClick={() => { setResetView("email"); setError(""); }} className="flex items-center gap-1 text-sm text-slate-500 hover:text-[#0B6E4F] mb-2">
                    <ArrowLeft className="h-4 w-4" /> Back
                  </button>
                  {resetMsg && <p className="text-sm text-[#0B6E4F] bg-[#F2F9F6] p-3 rounded-xl">{resetMsg}</p>}
                  <div>
                    <Label className="text-sm font-medium text-slate-700">Reset Code</Label>
                    <Input value={resetToken} onChange={(e) => setResetToken(e.target.value)} placeholder="Paste the code from your email" className="mt-1.5 h-12 rounded-xl bg-slate-50 border-slate-200 font-mono" data-testid="reset-token" required />
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-slate-700">New Password</Label>
                    <Input type="password" value={resetPassword} onChange={(e) => setResetPassword(e.target.value)} placeholder="Enter new password" className="mt-1.5 h-12 rounded-xl bg-slate-50 border-slate-200" data-testid="reset-password" required />
                  </div>
                  <Button type="submit" disabled={loading} data-testid="reset-submit-btn" className="w-full h-12 bg-[#0B6E4F] hover:bg-[#095A40] text-white rounded-full font-semibold">
                    {loading ? "Resetting..." : "Reset Password"}
                  </Button>
                </form>
              ) : (
              <form onSubmit={handleLogin} className="space-y-5">
                <div>
                  <Label htmlFor="login-email" className="text-sm font-medium text-slate-700">Email</Label>
                  <Input
                    id="login-email"
                    data-testid="login-email"
                    type="email"
                    placeholder="you@example.com"
                    value={loginForm.email}
                    onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                    className="mt-1.5 h-12 rounded-xl bg-slate-50 border-slate-200 focus:ring-2 focus:ring-[#0B6E4F]"
                    required
                  />
                </div>
                <div>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="login-password" className="text-sm font-medium text-slate-700">Password</Label>
                    <button type="button" onClick={() => { setResetView("email"); setError(""); }} className="text-xs text-[#0B6E4F] hover:underline font-medium" data-testid="forgot-password-link">Forgot password?</button>
                  </div>
                  <div className="relative mt-1.5">
                    <Input
                      id="login-password"
                      data-testid="login-password"
                      type={showPw ? "text" : "password"}
                      placeholder="Enter your password"
                      value={loginForm.password}
                      onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                      className="h-12 rounded-xl bg-slate-50 border-slate-200 pr-10 focus:ring-2 focus:ring-[#0B6E4F]"
                      required
                    />
                    <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-3.5 text-slate-400 hover:text-slate-600">
                      {showPw ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                </div>
                <Button
                  type="submit"
                  data-testid="login-submit-btn"
                  disabled={loading}
                  className="w-full h-12 bg-[#0B6E4F] hover:bg-[#095A40] text-white rounded-full font-semibold shadow-lg hover:shadow-xl transition-all"
                >
                  {loading ? "Signing in..." : "Sign In"} {!loading && <ArrowRight className="ml-2 h-4 w-4" />}
                </Button>
              </form>
              )}
            </TabsContent>

            <TabsContent value="register">
              <form onSubmit={handleRegister} className="space-y-5">
                <div>
                  <Label htmlFor="reg-name" className="text-sm font-medium text-slate-700">Full Name</Label>
                  <Input
                    id="reg-name"
                    data-testid="register-name"
                    placeholder="John Doe"
                    value={regForm.name}
                    onChange={(e) => setRegForm({ ...regForm, name: e.target.value })}
                    className="mt-1.5 h-12 rounded-xl bg-slate-50 border-slate-200 focus:ring-2 focus:ring-[#0B6E4F]"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="reg-email" className="text-sm font-medium text-slate-700">Email</Label>
                  <Input
                    id="reg-email"
                    data-testid="register-email"
                    type="email"
                    placeholder="you@example.com"
                    value={regForm.email}
                    onChange={(e) => setRegForm({ ...regForm, email: e.target.value })}
                    className="mt-1.5 h-12 rounded-xl bg-slate-50 border-slate-200 focus:ring-2 focus:ring-[#0B6E4F]"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="reg-password" className="text-sm font-medium text-slate-700">Password</Label>
                  <div className="relative mt-1.5">
                    <Input
                      id="reg-password"
                      data-testid="register-password"
                      type={showPw ? "text" : "password"}
                      placeholder="Create a strong password"
                      value={regForm.password}
                      onChange={(e) => setRegForm({ ...regForm, password: e.target.value })}
                      className="h-12 rounded-xl bg-slate-50 border-slate-200 pr-10 focus:ring-2 focus:ring-[#0B6E4F]"
                      required
                    />
                    <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-3.5 text-slate-400 hover:text-slate-600">
                      {showPw ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                </div>
                <Button
                  type="submit"
                  data-testid="register-submit-btn"
                  disabled={loading}
                  className="w-full h-12 bg-[#0B6E4F] hover:bg-[#095A40] text-white rounded-full font-semibold shadow-lg hover:shadow-xl transition-all"
                >
                  {loading ? "Creating account..." : "Create Account"} {!loading && <ArrowRight className="ml-2 h-4 w-4" />}
                </Button>
              </form>
            </TabsContent>

            <div className="mt-6">
              <div className="relative">
                <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-200" /></div>
                <div className="relative flex justify-center text-sm"><span className="bg-white px-4 text-slate-400">or continue with</span></div>
              </div>
              <Button
                onClick={handleGoogleLogin}
                data-testid="google-login-btn"
                variant="outline"
                className="w-full mt-4 h-12 rounded-full border-slate-200 hover:bg-slate-50 font-medium transition-all"
              >
                <svg className="mr-2 h-5 w-5" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
                Continue with Google
              </Button>
            </div>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
